import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Stack } from '@mui/material';
import PageHeader from 'components/Header/Page';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import moment from 'moment';
import { overAllCostData, workspaceGetEnv, fetchSectorList } from 'service/service';
import FiltersBar from './FilterBar';

// Table columns
const defaultColumnNames = [
  'Workspace ID',
  'Workspace Name',
  'Workspace URL',
  'Resource Group',
  'Consumed Service',
  'Meter Category',
  'Consumed Unit',
  'Project No.',
  'Project Name',
  'Business Sector',
  'Location',
  'Cost',
  'Subscription',
  'Billing Information',
  'Job Run Date',
  'Month',
  'Consumed Quantity',
  'Compute Source',
  'Environment',
  'Sector',
  'Cluster Name',
];

// Helper function to safely access row data with case-insensitive fallback
const getRowValue = (row: any, ...keys: string[]) => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) {
      return row[key];
    }
  }
  return '--';
};
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  compactDisplay: 'short',
  maximumFractionDigits: 2
});
function getColumns() {
  return [
    {
      key: 'workspaceID',
      name: 'Workspace ID',
      formatter({ row }: any) {
        return <div>{getRowValue(row, 'workspaceID', 'WorkspaceID', 'workspace_id', 'workspaceId')}</div>;
      },
      minWidth: 140
    },
    {
      key: 'workspaceName',
      name: 'Workspace Name',
      formatter({ row }: any) {
        return <div>{getRowValue(row, 'workspaceName', 'WorkspaceName', 'workspace_name', 'workspaceName')}</div>;
      },
      minWidth: 140
    },
    {
      key: 'workspaceURL',
      name: 'Workspace URL',
      formatter({ row }: any) {
        const rawUrl = row?.workspace_url ?? row?.workspaceURL ?? row?.WorkspaceURL ?? row?.workspaceUrl;
        const fullUrl = rawUrl && !String(rawUrl).startsWith('http')
          ? `https://${rawUrl}`
          : rawUrl;

        // Extract portion after https:// and before first dot
        let displayText = '--';
        if (fullUrl) {
          const match = String(fullUrl).match(/^https?:\/\/([^.]+)/);
          displayText = match ? match[1] : fullUrl;
        }

        return (
          <div title={fullUrl}>
            {fullUrl ? (
              <a
                href={fullUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#4fc3f7', textDecoration: 'underline' }}
              >
                {displayText}
              </a>
            ) : '--'}
          </div>
        );
      },
      minWidth: 200
    },
    {
      key: 'resourceGroup',
      name: 'Resource Group',
      formatter({ row }: any) {
        return <div>{getRowValue(row, 'resourceGroup', 'ResourceGroup', 'resource_group')}</div>;
      },
      minWidth: 160
    },
    {
      key: 'consumedService',
      name: 'Consumed Service',
      formatter({ row }: any) {
        return <div>{getRowValue(row, 'consumedService', 'ConsumedService', 'consumed_service')}</div>;
      },
      minWidth: 200
    },
    {
      key: 'meterCategory',
      name: 'Meter Category',
      formatter({ row }: any) {
        return <div>{getRowValue(row, 'meterCategory', 'MeterCategory', 'meter_category')}</div>;
      },
      minWidth: 140
    },
    {
      key: 'consumedUnit',
      name: 'Consumed Unit',
      formatter({ row }: any) {
        return <div>{getRowValue(row, 'consumedUnit', 'ConsumedUnit', 'consumed_unit')}</div>;
      },
      minWidth: 120
    },
    {
      key: 'projectNo',
      name: 'Project No.',
      formatter({ row }: any) {
        return <div>{getRowValue(row, 'projectNo', 'ProjectNo', 'project_no', 'projectNumber')}</div>;
      },
      minWidth: 120
    },
    {
      key: 'projectName',
      name: 'Project Name',
      formatter({ row }: any) {
        return <div>{getRowValue(row, 'projectName', 'ProjectName', 'project_name')}</div>;
      },
      minWidth: 120
    },
    {
      key: 'businessSector',
      name: 'Business Sector',
      formatter({ row }: any) {
        return <div>{getRowValue(row, 'businessSector', 'BusinessSector', 'business_sector')}</div>;
      },
      minWidth: 120
    },
    {
      key: 'location',
      name: 'Location',
      formatter({ row }: any) {
        return <div>{getRowValue(row, 'location', 'Location')}</div>;
      },
      minWidth: 140
    },
    {
      key: 'cost',
      name: 'Cost',
      formatter({ row }: any) {
        const v = row?.cost ?? row?.finalCost ?? row?.Cost ?? row?.FinalCost;
        const num = typeof v === 'string' ? Number(v) : v;
        return <div>{(typeof num === 'number' && Number.isFinite(num)) ? currencyFormatter.format(num) : (v ?? '--')}</div>;
      },
      minWidth: 120,
      align: 'left'
    },
    {
      key: 'subscription',
      name: 'Subscription',
      formatter({ row }: any) {
        const val = getRowValue(row, 'subscription', 'Subscription');
        return <div title={val}>{val}</div>;
      },
      minWidth: 220
    },
    {
      key: 'billingInformation',
      name: 'Billing Information',
      formatter({ row }: any) {
        const val = getRowValue(row, 'billingInformation', 'BillingInformation', 'billing_information');
        return <div title={val}>{val}</div>;
      },
      minWidth: 220
    },
    {
      key: 'jobRunDate',
      name: 'Job Run Date',
      formatter({ row }: any) {
        const val = getRowValue(row, 'jobRunDate', 'JobRunDate', 'job_run_date');
        return <div title={val}>{val}</div>;
      },
      minWidth: 220
    },
    {
      key: 'month',
      name: 'Month',
      formatter({ row }: any) {
        return <div>{getRowValue(row, 'month', 'Month')}</div>;
      },
      minWidth: 120
    },
    {
      key: 'consumedQuantity',
      name: 'Consumed Quantity',
      formatter({ row }: any) {
        const q = row?.consumedQuantity ?? row?.ConsumedQuantity ?? row?.consumed_quantity;
        return <div>{typeof q === 'number' ? q.toLocaleString() : q || '--'}</div>;
      },
      minWidth: 120,
      align: 'left'
    },
    {
      key: 'computeSource',
      name: 'Compute Source',
      formatter({ row }: any) {
        return <div>{getRowValue(row, 'computeSource', 'ComputeSource', 'compute_source')}</div>;
      },
      minWidth: 140
    },
    {
      key: 'environment',
      name: 'Environment',
      formatter({ row }: any) {
        return <div>{getRowValue(row, 'environment', 'Environment')}</div>;
      },
      minWidth: 120
    },
    {
      key: 'sector',
      name: 'Sector',
      formatter({ row }: any) {
        return <div>{getRowValue(row, 'sector', 'Sector')}</div>;
      },
      minWidth: 120
    },
    {
      key: 'clusterName',
      name: 'Cluster Name',
      formatter({ row }: any) {
        const val = getRowValue(row, 'clusterName', 'ClusterName', 'cluster_name');
        return <div title={val}>{val}</div>;
      },
      minWidth: 220
    },
  ];
}

// Region and sector options
const regions = [
  'ALL',
  'Brazil South',
  'Australia East',
  'UK South',
  'South Central US',
  'East US',
  'Germany West Central'
];
const regionOptions = regions.map(location => ({ label: location, value: location }));

const sectorOptionsList = [
  'latam', 'apac', 'TBD', 'amesa', 'essa', 'flna', 'global', 'tbd', 'pbna', 'pfna', 'snt'
];

const envOptionsDefault = [
  { label: 'ALL', value: 'both' },
  { label: 'PROD', value: 'prod' },
  { label: 'NON-PROD', value: 'non-prod' }
];

// Helper function to collect all records from day objects
function collectAllRecords(data: any, label: string, type: 'year' | 'month' | 'week' | 'day'): any[] {
  let allRecords: any[] = [];

  const normalizeRoot = (input: any) => {
    if (!input) return input;
    if (typeof input === 'object' && 'kind' in input && 'node' in input) {
      return input.node;
    }
    return input;
  };

  const unwrap = (obj: any, prefixKey?: string, nameField?: string) => {
    if (!obj || typeof obj !== 'object') return obj;
    if (nameField && (nameField in obj)) return obj;
    if (prefixKey && prefixKey in obj && typeof obj[prefixKey] === 'object') return obj[prefixKey];
    const keys = Object.keys(obj);
    if (keys.length === 1 && prefixKey && keys[0].startsWith(prefixKey) && typeof obj[keys[0]] === 'object') {
      return obj[keys[0]];
    }
    return obj;
  };

  function collectFromDayObj(dayObj: any) {
    if (!dayObj) return;
    const inner = unwrap(dayObj, 'day', 'day_name');
    if (inner && Array.isArray(inner.records)) {
      allRecords = allRecords.concat(inner.records);
    } else if (inner && Array.isArray(inner.data)) {
      allRecords = allRecords.concat(inner.data);
    }
  }

  function collectFromWeekObj(weekObj: any) {
    if (!weekObj) return;
    const weekData = unwrap(weekObj, 'week', 'week_name');
    if (weekData && Array.isArray(weekData.children)) {
      weekData.children.forEach((dayWrapper: any) => collectFromDayObj(dayWrapper));
    }
  }

  function collectFromMonthObj(monthObj: any) {
    if (!monthObj) return;
    const monthData = unwrap(monthObj, 'month', 'month_name');
    if (monthData && Array.isArray(monthData.children)) {
      monthData.children.forEach((weekWrapper: any) => collectFromWeekObj(weekWrapper));
    }
  }

  function collectFromYearObj(yearObj: any) {
    if (!yearObj) return;
    const yearData = unwrap(yearObj, 'year', 'year_name');
    if (yearData && Array.isArray(yearData.children)) {
      yearData.children.forEach((monthWrapper: any) => collectFromMonthObj(monthWrapper));
    }
  }

  function matchAndCollect(obj: any) {
    if (!obj || typeof obj !== 'object') return false;

    const yearCandidate = unwrap(obj, 'year', 'year_name');
    if (type === 'year') {
      if (yearCandidate?.year_name === label) {
        collectFromYearObj(obj);
        return true;
      }
    }

    const monthCandidate = unwrap(obj, 'month', 'month_name');
    if (type === 'month') {
      if (monthCandidate?.month_name === label) {
        collectFromMonthObj(obj);
        return true;
      }
    }

    if (type === 'week') {
      const wk = unwrap(obj, 'week', 'week_name');
      if (wk?.week_name === label) {
        collectFromWeekObj(obj);
        return true;
      }
    }

    if (type === 'day') {
      const day = unwrap(obj, 'day', 'day_name');
      if (day?.day_name === label || day?.date === label) {
        collectFromDayObj(obj);
        return true;
      }
    }

    return false;
  }

  function recurse(obj: any) {
    const root = normalizeRoot(obj);
    if (Array.isArray(root)) {
      for (const item of root) {
        if (matchAndCollect(item)) continue;
        recurse(item);
      }
      return;
    }

    if (root && typeof root === 'object') {
      if (matchAndCollect(root)) return;
      for (const v of Object.values(root)) {
        recurse(v);
      }
    }
  }

  recurse(data);
  return allRecords;
}

const OverallCostDetailsPage = () => {
  const routerLocation = useLocation();
  const navigate = useNavigate();

  interface NavigationState {
    dateLabel?: string;
    apiData?: any;
    type?: 'year' | 'month' | 'week' | 'day';
    startDate?: string;
    endDate?: string;
    env?: string;
    sector?: string;
    location?: string;
    selectedDateInString?: string;
    pageHeader?: string;
    pageSubHeader?: string;
    searchSource?: string;
    contentName?: string;
    filters?: any;
    detailedTableTitle?: string;
    detailedTableType?: string;
  }

  // Get data from navigation state
  const navigationState = routerLocation.state as NavigationState | null;
  const {
    dateLabel,
    apiData,
    type = 'day',
    startDate: navStartDate,
    endDate: navEndDate,
    env: navEnv,
    sector: navSector,
    location: navLocation,
    selectedDateInString: navSelectedDate,
    pageHeader,
    pageSubHeader,
    searchSource: navSearchSource
  } = (navigationState || {});

  const [sector, setSector] = useState<string>(navSector || 'all');
  const [env, setEnv] = useState(navEnv || 'both');
  const [envOptionsState, setEnvOptionsState] = useState<{ label: string; value: string }[]>(envOptionsDefault);
  const [, setLoaderEnv] = useState(false);
  const initialEnvAppliedRef = useRef(false);
  const envRef = useRef(env);
  useEffect(() => { envRef.current = env; }, [env]);
  const [locationFilter, setLocationFilter] = useState(navLocation || 'South Central US');
  const [sectorName, setSectorName] = useState(navSector || 'all');
  const yesterday = moment().tz('Asia/Kolkata').subtract(1, 'day');
  const [startDate, setStartDate] = useState(
    navStartDate || yesterday.clone().startOf('day').format('YYYY-MM-DD')
  );
  const [endDate, setEndDate] = useState(
    navEndDate || yesterday.endOf('day').format('YYYY-MM-DD')
  );
  const [selectedDateInString, setSelectedDateInString] = useState(navSelectedDate || '3M');
  const [detailRows, setDetailRows] = useState<any[]>([]);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [sortingInfo, setSortingInfo] = useState<{ sortBy: string; sortDir: 'asc' | 'desc' | '' }>({ sortBy: '', sortDir: '' });
  const [, setFilterInfo] = useState([]);
  const [sectorList, setSectorList] = useState<string[]>([]);

  const fetchSectors = async () => {
    const resp = await fetchSectorList({});
    if (resp?.success) {
      setSectorList(resp?.data || []);
    }
  };

  useEffect(() => {
    fetchSectors()
  }, [])


  const handleExportRequest = async (preferredColumns?: any) => {
    try {
      const cols = getColumns();
      const keyToNameMap: Record<string, string> = {};
      cols.forEach((c: any) => {
        if (c?.key) keyToNameMap[String(c.key)] = c.name || String(c.key);
        if (c?.name) keyToNameMap[String(c.name)] = c.name;
      });
      const fallbackMap: Record<string, string> = {
        cost: 'Cost',
        resource: 'Resource',
        consumedQuantity: 'Consumed Quantity',
        jobRunDate: 'Job Run Date'
      };

      let apiSortBy: string | undefined = undefined;
      if (sortingInfo?.sortBy) {
        const key = String(sortingInfo.sortBy);
        apiSortBy = keyToNameMap[key] || fallbackMap[key] || undefined;
      }

      const exportPage = 1;
      const exportPageSize = 10000;

      const resp: any = await overAllCostData({
        startDate,
        endDate,
        env,
        sector,
        location: locationFilter,
        selectedDateInString,
        page: exportPage,
        pageSize: exportPageSize,
        exportType: 'excel',
        sortField: apiSortBy,
        sortOrder: sortingInfo.sortDir || undefined
      });

      if (!resp || resp?.success === false) {
        console.error('Export failed', resp?.error || resp);
      }
    } catch (err) {
      console.error('Export error', err);
    }
  };

  const updateHistoryState = (overrides: Partial<{
    startDate: string;
    endDate: string;
    env: string;
    sector: string;
    location: string;
    selectedDateInString: string;
    filters?: any;
    contentName?: string;
    workspaceName?: string;
    warehouseName?: string;
    warehouseType?: string;
    userExecutedBy?: string;
    catalogName?: string;
    searchSource?: string;
  }>) => {
    const newState = {
      startDate,
      endDate,
      env,
      sector,
      location: locationFilter,
      selectedDateInString,
      pageHeader,
      pageSubHeader,
      searchSource: navSearchSource,
      contentName: navigationState?.contentName,
      filters: navigationState?.filters,
      detailedTableTitle: navigationState?.detailedTableTitle,
      detailedTableType: navigationState?.detailedTableType,
      ...overrides
    };
    navigate(routerLocation.pathname, { state: newState, replace: true });
  };

  useEffect(() => {
    if (routerLocation.state) {
      if (navStartDate) setStartDate(navStartDate);
      if (navEndDate) setEndDate(navEndDate);
      if (navEnv) setEnv(navEnv);
      if (navSector) {
        setSector(navSector);
        setSectorName(navSector);
      }
      if (navLocation) setLocationFilter(navLocation);
      if (navSelectedDate) setSelectedDateInString(navSelectedDate);
    }
  }, []);

  const getEnvFetch = async (loc: string) => {
    try {
      setLoaderEnv(true);
      const res = await workspaceGetEnv(loc);
      const options = [{ label: 'ALL', value: 'both' }, ...(res?.data || [])];
      setEnvOptionsState(options);

      if (!initialEnvAppliedRef.current && navEnv) {
        setEnv(navEnv);
        initialEnvAppliedRef.current = true;
      } else {
        const availableValues = options.map(o => o.value);
        const currentEnv = envRef.current;
        if (!availableValues.includes(currentEnv)) {
          const fallback = availableValues.includes('both') ? 'both' : (availableValues[0] || 'both');
          setEnv(fallback);
        }
      }
    } catch (error) {
      console.error('Failed to fetch environment options:', error);
    } finally {
      setLoaderEnv(false);
    }
  };

  useEffect(() => {
    getEnvFetch(locationFilter);
  }, []);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [serverPagination, setServerPagination] = useState(false);
  const [serverPaginationData, setServerPaginationData] = useState<{ totalElements: number; totalPages: number; page?: number; pageSize?: number }>({
    totalElements: 0,
    totalPages: 0,
    page: 1,
    pageSize: 10
  });

  // ✅ Helper to map sorting info
  const getApiSortField = (sortBy: string) => {
    const cols = getColumns();
    const keyToNameMap: Record<string, string> = {};
    cols.forEach((c: any) => {
      if (c?.key) keyToNameMap[String(c.key)] = c.name || String(c.key);
      if (c?.name) keyToNameMap[String(c.name)] = c.name;
    });

    const fallbackMap: Record<string, string> = {
      cost: 'Cost',
      resource: 'Resource',
      consumedQuantity: 'Consumed Quantity',
      jobRunDate: 'Job Run Date'
    };

    return keyToNameMap[sortBy] || fallbackMap[sortBy] || undefined;
  };

  // ✅ Helper to update pagination state
  const updatePaginationState = (pagination: any, rowsCount: number) => {
    if (pagination) {
      setServerPagination(true);
      setServerPaginationData({
        totalElements: pagination.total ?? rowsCount,
        totalPages: pagination.totalPages ?? Math.ceil((pagination.total ?? rowsCount) / pageSize),
        page: pagination.page ?? page,
        pageSize: pagination.pageSize ?? pageSize
      });
      if (typeof pagination.page === 'number' && pagination.page !== page) {
        setPage(pagination.page);
      }
    } else {
      setServerPagination(false);
      setServerPaginationData({
        totalElements: rowsCount,
        totalPages: Math.ceil(rowsCount / pageSize),
        page,
        pageSize
      });
    }
  };

  useEffect(() => {
    let cancelled = false;

    const resolveDetailData = async () => {
      const apiSortBy = sortingInfo?.sortBy ? getApiSortField(String(sortingInfo.sortBy)) : undefined;

      if (startDate && endDate) {
        const resp: any = await overAllCostData({
          startDate,
          endDate,
          env,
          sector,
          location: locationFilter,
          selectedDateInString,
          page,
          pageSize,
          sortField: apiSortBy,
          sortOrder: sortingInfo.sortDir || undefined
        });
        return {
          rows: resp?.success && resp.data?.data ? resp.data.data : [],
          pagination: resp?.data?.pagination
        };
      }

      if (apiData && dateLabel) {
        return {
          rows: collectAllRecords(apiData, dateLabel, type),
          pagination: null
        };
      }

      return { rows: [], pagination: null };
    };

    async function loadDetails() {
      try {
        setIsFetchingDetails(true);
        const { rows, pagination } = await resolveDetailData();
        if (cancelled) return;

        setDetailRows(rows);
        updatePaginationState(pagination, rows.length);
      } catch (err) {
        console.error('Failed to fetch detail rows', err);
        if (!cancelled) setDetailRows([]);
      } finally {
        if (!cancelled) setIsFetchingDetails(false);
      }
    }

    loadDetails();
    return () => { cancelled = true; };
  }, [startDate, endDate, env, sector, locationFilter, selectedDateInString, apiData, dateLabel, type, page, pageSize, sortingInfo]);

  const handleSorting = (newSorting: { sortBy: string; sortDir: 'asc' | 'desc' | '' }) => {
    if (newSorting?.sortBy && (newSorting.sortDir === 'asc' || newSorting.sortDir === 'desc')) {
      setSortingInfo({
        sortBy: newSorting.sortBy,
        sortDir: newSorting.sortDir || ''
      });
    } else {
      setSortingInfo({ sortBy: '', sortDir: '' });
    }
  };

  const handleFilter = (filterInfo: any) => {
    setFilterInfo(filterInfo);
  };

  const tableRecords = useMemo(() => {
    if (detailRows && detailRows.length) {
      return detailRows;
    }
    if (!apiData || !dateLabel) return [];
    return collectAllRecords(apiData, dateLabel, type);
  }, [apiData, dateLabel, type, detailRows]);

  const paginatedRecords = useMemo(() => {
    if (serverPagination) return tableRecords;
    const startIdx = (page - 1) * pageSize;
    return tableRecords.slice(startIdx, startIdx + pageSize);
  }, [tableRecords, page, pageSize, serverPagination]);

  const paginationData = useMemo(() => {
    if (serverPagination) {
      return {
        totalElements: serverPaginationData.totalElements,
        totalPages: Math.ceil(serverPaginationData.totalPages)
      };
    }
    return {
      totalElements: tableRecords.length,
      totalPages: Math.ceil(tableRecords.length / pageSize)
    };

  }, [serverPagination, serverPaginationData, tableRecords.length, pageSize]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  const handleDateSelect = (stDt: string, edDt: string, selectedDate?: string) => {
    const selected = selectedDate || 'Hrs';
    setSelectedDateInString(selected);

    const yesterday = moment().tz('Asia/Kolkata').subtract(1, 'day');
    const end = yesterday.clone().endOf('day');
    let start;

    if (selected === 'Hrs') {
      start = yesterday.clone().startOf('day');
    } else if (selected === '7d') {
      start = yesterday.clone().subtract(6, 'days').startOf('day');
    } else if (selected === '30d') {
      start = yesterday.clone().subtract(29, 'days').startOf('day');
    } else if (selected === '1M') {
      start = yesterday.clone().subtract(1, 'month').startOf('day');
    } else if (selected === '3M') {
      start = yesterday.clone().subtract(3, 'months').startOf('day');
    } else if (selected === '6M') {
      start = yesterday.clone().subtract(6, 'months').startOf('day');
    } else if (selected === '1Y') {
      start = yesterday.clone().subtract(1, 'year').startOf('day');
    } else if (selected === 'custom') {
      start = moment(stDt).startOf('day');
      const customEnd = moment(edDt).endOf('day');
      setStartDate(start.format('YYYY-MM-DD'));
      setEndDate(customEnd.format('YYYY-MM-DD'));
      return;
    } else {
      start = moment(stDt).startOf('day');
    }

    setStartDate(start.format('YYYY-MM-DD'));
    setEndDate(end.format('YYYY-MM-DD'));
  };


  return (
    <Box sx={{ mt: 12 }}>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box sx={{ mb: 2, fontSize: '1.125rem', fontWeight: '600', color: '#003771' }}>
          <span
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/databricksADB/workspace', {
              state: {
                startDate,
                endDate,
                env,
                sector: sectorName,
                location: locationFilter,
                selectedDateInString
              }
            })}
          >
            Workspace Overview
          </span>
          {' > '}
          <span
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/databricksADB/overall-cost', {
              state: {
                startDate,
                endDate,
                env,
                sector: sectorName,
                location: locationFilter,
                selectedDateInString
              }
            })}
          >
            Overall Cost
          </span>
          {' > '}
          <span>{dateLabel}</span>
          {((selectedDateInString === 'customDateRange')
            && (startDate && endDate && startDate !== endDate)) && (
              <span style={{ marginLeft: 8, color: '#003771', fontSize: '0.95rem' }}>
                ({`${startDate} to ${endDate}`})
              </span>
            )}
        </Box>
        <Box sx={{ zIndex: 1 }}>
          <PageHeader title=" " icon={''} lastSync={null}>
            <FiltersBar
              location={locationFilter}
              setLocation={setLocationFilter}
              env={env}
              setEnv={setEnv}
              envOptions={envOptionsState}
              setEnvOptions={setEnvOptionsState}
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              handleDateSelect={handleDateSelect}
              selectedDateInString={selectedDateInString}
              setSelectedDateInString={setSelectedDateInString}
              sectorName={sectorName}
              setSectorName={(v) => {
                const resolved = typeof v === 'function' ? (v as (prev: string) => string)(sectorName) : v;
                setSectorName(resolved);
                setSector(resolved);
              }}
              updateHistoryState={updateHistoryState}
              searchSource={navSearchSource}
              sectorOptionsList={sectorList}
            />
          </PageHeader>
        </Box>
      </Stack>
      {paginationData && paginationData.totalElements != 0 && <CustomizedDataGrid
        title={dateLabel}
        tableColumns={getColumns()}
        tableData={paginatedRecords}
        tableType="ovearall-cost-details-ver"
        defaultColumnNames={defaultColumnNames}
        page={serverPagination ? (serverPaginationData.page ?? page) : page}
        pageSize={serverPagination ? (serverPaginationData.pageSize ?? pageSize) : pageSize}
        paginationData={paginationData}
        listLength={serverPagination ? (serverPaginationData.totalElements ?? 0) : tableRecords.length}
        handlepageSizeChange={handlePageSizeChange}
        handlePageChange={handlePageChange}
        isDateRangeFilter={false}
        isDisabled={false}
        isFetching={isFetchingDetails}
        showExport={true}
        exportTableData={handleExportRequest}
        onSorting={handleSorting}
        currSorting={sortingInfo}
        onFilterCheck={handleFilter}
      />}
      {paginationData && paginationData.totalElements === 0 && (
        <Box sx={{ mt: 4, textAlign: 'center', color: '#666' }}>
          No data available for the selected filters.
        </Box>
      )}
    </Box>
  );
};

export default OverallCostDetailsPage;
